import { z } from 'zod';

/** Query parameter on referral signup links (`?mgmCode=…`). */
export const REGISTRATION_MGM_QUERY_PARAM = 'mgmCode';

/** Safe status marker after a referral code is stripped from the URL. */
export const REFERRAL_STATUS_QUERY_PARAM = 'referralStatus';

/** Authenticated landing page that resolves an MGM invitation. */
export const ACCOUNT_MGM_PATH = '/account/mgm';

/** Advocate landing page used by authenticated shoppers and legacy email links. */
export const MEMBER_GET_MEMBER_PATH = '/invite-a-friend';

/** Query flag after successful registration (`?registered=true&email=…`). */
export const REGISTRATION_CONFIRMED_QUERY_PARAM = 'registered';

/** Query parameter on registration-light signup links (`?lrCode=…`). */
export const REGISTRATION_LIGHT_QUERY_PARAM = 'lrCode';

const LEGACY_REFERRAL_QUERY_PARAMS: Record<string, string> = {
    rlCode: REGISTRATION_LIGHT_QUERY_PARAM,
    registrationMgmCode: REGISTRATION_MGM_QUERY_PARAM,
};

const registrationConfirmationEmailSchema = z.string().trim().email();

export type RegistrationConfirmationParseResult =
    | { showConfirmation: true; confirmationEmail: string }
    | { showConfirmation: false; invalidConfirmationRequest: boolean };

export type RegistrationReferralData = {
    registrationMgmCode?: string;
    registrationLightCode?: string;
};

export type RegistrationMgmReferralData = {
    registrationMgmCode: string;
};

/** Returns a full URL with registration referral params and PII query params removed for analytics tracking. */
export function stripRegistrationReferralParamsFromUrl(url: string): string {
    const parsed = new URL(url);
    const registrationReferralQueryParams = [
        REGISTRATION_MGM_QUERY_PARAM,
        REGISTRATION_CONFIRMED_QUERY_PARAM,
        REGISTRATION_LIGHT_QUERY_PARAM,
    ];
    for (const param of registrationReferralQueryParams) {
        parsed.searchParams.delete(param);
    }
    parsed.searchParams.delete('email');
    if (parsed.pathname.endsWith(MEMBER_GET_MEMBER_PATH)) {
        parsed.searchParams.delete('id');
    }
    return parsed.toString();
}

/** Returns the params with legacy referral names renamed, or null when none is present. */
export function renameLegacyReferralParams(searchParams: URLSearchParams): URLSearchParams | null {
    if (!Object.keys(LEGACY_REFERRAL_QUERY_PARAMS).some((legacy) => searchParams.has(legacy))) {
        return null;
    }
    const renamed = new URLSearchParams();
    for (const [key, value] of searchParams) {
        const current = LEGACY_REFERRAL_QUERY_PARAMS[key];
        if (!current) {
            renamed.append(key, value);
        } else if (!searchParams.has(current)) {
            renamed.append(current, value);
        }
    }
    return renamed;
}

export function parseRegistrationReferralFromSearchParams(searchParams: URLSearchParams): RegistrationReferralData {
    const registrationMgmCode = searchParams.get(REGISTRATION_MGM_QUERY_PARAM)?.trim();
    const registrationLightCode = searchParams.get(REGISTRATION_LIGHT_QUERY_PARAM)?.trim();

    return {
        ...(registrationMgmCode ? { registrationMgmCode } : {}),
        ...(registrationLightCode ? { registrationLightCode } : {}),
    };
}

/** Extracts MGM referral data suitable for httpOnly cookie persistence. */
export function extractMgmReferralData(
    data: RegistrationReferralData | null | undefined
): RegistrationMgmReferralData | null {
    const registrationMgmCode = data?.registrationMgmCode?.trim();
    return registrationMgmCode ? { registrationMgmCode } : null;
}

/** Merges MGM referral codes for cookie storage (LightCode is never persisted). */
export function mergeMgmReferralCookieData(
    stored: RegistrationMgmReferralData | null | undefined,
    incoming: RegistrationMgmReferralData
): RegistrationMgmReferralData | null {
    const registrationMgmCode = incoming.registrationMgmCode.trim() || stored?.registrationMgmCode;
    return registrationMgmCode ? { registrationMgmCode } : null;
}

export function parseRegistrationConfirmationFromSearchParams(
    searchParams: URLSearchParams
): RegistrationConfirmationParseResult {
    if (searchParams.get(REGISTRATION_CONFIRMED_QUERY_PARAM) !== 'true') {
        return { showConfirmation: false, invalidConfirmationRequest: false };
    }

    const email = searchParams.get('email')?.trim() ?? '';
    const parsed = registrationConfirmationEmailSchema.safeParse(email);
    if (!parsed.success) {
        return { showConfirmation: false, invalidConfirmationRequest: true };
    }

    return { showConfirmation: true, confirmationEmail: parsed.data };
}
